<script lang="ts" setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { useEvents } from '@/plugins/events';
import { Form } from '@/helpers/Form';
import { debounce } from 'lodash';
import Pager from '@/helpers/Pager';
import AutocompleteService, {
    AutocompleteFilterModel,
    FetchSettings,
} from '@/modules/core/common/services/AutocompleteService';
import IdeoDropdown from "@/components/ideo/dropdown/IdeoDropdown.vue";

const emits = defineEmits(["update:modelValue"]);

const props = defineProps({
  "modelValue": null,
  "label": null,
  "placeholder": null,
  "sitemapId": null,
  "extraParams": null,
  "fetchSettings": null,
  "multiselect": { type: Boolean,  }
});

const { $events } = useEvents();

const type = ref(1);
const userMode = ref<'selectedUser' | 'currentUser'>('selectedUser');
const fetching = ref(false);
const alreadyFetched = ref(false);
const options = ref<Record<string, any>[]>([]);

const select = ref<InstanceType<typeof IdeoDropdown>>(null);
const searchBar = ref<HTMLInputElement | null>(null);

const filter = reactive(
    Form.create<AutocompleteFilterModel>({
        search: '',
    })
);

const pager = reactive(new Pager(1, 99, '', 'ASC')) as Pager;

const value = computed(() => props.modelValue);
const variant = computed(() =>  opened.value || value.value ? 'primary' : 'secondary');
const opened = computed(() => select.value?.visible);
const isUserType = computed(() => type.value === 1);

const clearSearchData = () =>
{
    if (filter.search)
    {
        filter.reset();
        alreadyFetched.value = false;
        init();
    }
};

const clearModelData = () =>
{
    userMode.value = 'selectedUser';
    emits('update:modelValue', null);
    $events.$emit("refetchData", { resetPageIndex: true });
};

const searchData = debounce(async () =>
{
    init();
}, 600);

const isSelected = (option: Record<string, any>) =>
{
    if (props.modelValue && props.modelValue?.key === option.key) return true;

    return false;
};

const setNewValue = (option: Record<string, any>) =>
{
    userMode.value = 'selectedUser';
    emits('update:modelValue', option);

    select.value?.hide();
};

const onUserModeChanged = () =>
{
    if (!isUserType.value || userMode.value !== 'currentUser')
    {
        if (value.value?.key === 'CurrentUser,1') emits('update:modelValue', null);

        return;
    }

    emits('update:modelValue', { key: 'CurrentUser,1', value: 'Zalogowany użytkownik' });
};

const fetchOptions = async () =>
{
    try
    {
        const response = await AutocompleteService.fetchOptions(
            props.fetchSettings.module,
            'filter',
            props.fetchSettings.propType,
            filter.data(),
            pager,
            { type: type.value },
            null,
            props.fetchSettings.actionName,
            props.sitemapId,
            props.extraParams
        );

        pager.setTotalRows(response.totalRows);

        options.value = response.items.map((item) => ({ key: `${item.result.key},${type.value}`, value: item.result.value }));
    }
    catch (ex)
    {
        options.value = [];
    }
};

const show = async () =>
{
    if (!alreadyFetched.value) init();

    await nextTick();
    await nextTick();

    searchBar.value?.focus();
};

const hide = () =>
{
    clearSearchData();
};

const init = async () =>
{
    alreadyFetched.value = false;

    await fetchOptions();

    alreadyFetched.value = true;
};

watch(
    () => type.value,
    async () =>
    {
        if (!isUserType.value) userMode.value = 'selectedUser';

        if (value.value && !String(value.value.key).endsWith(`,${type.value}`)) emits('update:modelValue', null);

        if (isUserType.value && value.value?.key === 'CurrentUser,1') userMode.value = 'currentUser';

        init();
    }
);

watch(
    () => props.modelValue,
    (newValue) =>
    {
        userMode.value = newValue?.key === 'CurrentUser,1' ? 'currentUser' : 'selectedUser';
    },
    { immediate: true }
);
</script>

<template>
    <div class="filter-select filter-dropdown-object">
        <ideo-dropdown
            ref="select"
            boundry="body"
            :variant="variant"
            no-caret
            dropdown
            :top-offset="6"
            @show="show"
            @hide="hide"
        >
            <template #button-content>
                <div class="label-value">
                    <span v-if="label && value" class="label">{{ label }}</span>
                    <span class="value">{{ value?.value ?? placeholder }}</span>
                </div>
                <i class="fas fa-chevron-down arrow ms-2" :class="{ 'opened-arrow': opened }"></i>
                <div v-if="value" class="btn-remove ms-2" @click.stop="clearModelData">
                    <i class="fas fa-times"></i>
                </div>
            </template>
            <template #default>
                <div class="radius-4 pt-1">
                    <div class="dropdown__input-row">
                        <ideo-form-radio-group class="mb-2" stacked>
                            <ideo-form-radio v-model="type" name="filter-type" :value="1">
                                {{ $t('[[[Użytkownik]]]') }}
                            </ideo-form-radio>
                            <ideo-form-radio v-model="type" name="filter-type" :value="3">
                                {{ $t('[[[Dział]]]') }}
                            </ideo-form-radio>
                            <ideo-form-radio v-model="type" name="filter-type" :value="4">
                                {{ $t('[[[Zespól]]]') }}
                            </ideo-form-radio>
                            <ideo-form-radio v-model="type" name="filter-type" :value="5">
                                {{ $t('[[[Grupa]]]') }}
                            </ideo-form-radio>
                        </ideo-form-radio-group>
                        <div v-if="isUserType" class="filter-active-target__user-mode">
                            <ideo-form-radio-group class="mb-0" stacked>
                                <ideo-form-radio v-model="userMode" name="user-mode" value="currentUser" @change="onUserModeChanged">
                                    {{ $t('[[[Zalogowany użytkownik]]]') }}
                                </ideo-form-radio>
                                <ideo-form-radio v-model="userMode" name="user-mode" value="selectedUser" @change="onUserModeChanged">
                                    {{ $t('[[[Wybrany użytkownik]]]') }}
                                </ideo-form-radio>
                            </ideo-form-radio-group>
                        </div>
                        <div v-if="!isUserType || userMode === 'selectedUser'" class="dropdown__input-wrapper">
                            <input
                                v-model="filter.search"
                                ref="searchBar"
                                type="text"
                                :placeholder="$t('[[[Wyszukaj...]]]')"
                                class="me-2 search-bar flex-grow-1"
                                @input="searchData"
                            />
                            <!-- @keydown="focusOnList" -->
                            <span class="border-0 bg-transparent p-1">
                                <i v-if="!filter.search" class="fas fa-search"></i>
                                <button
                                    v-else
                                    class="border-0 p-0 bg-transparent clear-button"
                                    style="color: inherit"
                                    @click.stop="clearSearchData"
                                >
                                    <span class="fas fa-times"></span>
                                </button>
                            </span>
                        </div>
                    </div>
                    <!-- @scroll="listHasBeenScrolled" -->
                    <div v-if="!isUserType || userMode === 'selectedUser'" class="dropdown__list-content scroll">
                        <template v-if="!fetching && options.length">
                            <div
                                v-for="(item, index) in options"
                                :key="index"
                                tabindex="0"
                                class="dropdown__list-content__item"
                                :class="{ 'selected-option': isSelected(item) }"
                                @click="setNewValue(item)"
                            >
                                {{ item.value || $t("[[[Brak wartości wyświetlanej]]]") }}
                            </div>
                        </template>
                        <template v-else-if="options.length">
                            <div class="fetching-indicator">
                                <div class="lds-dual-ring"></div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="d-flex flex-column align-items-center p-3">
                                <i class="fas fa-question-square fa-2x"></i>
                                <span class="mt-2 h5">{{ $t('[[[Brak wyników!]]]') }}</span>
                            </div>
                        </template>
                    </div>
                </div>
            </template>
        </ideo-dropdown>
    </div>
</template>

<style scoped>
.filter-active-target__user-mode {
    margin-top: 0.5rem;
    padding-top: 0.625rem;
    border-top: 1px solid var(--bs-border-color, rgba(0, 0, 0, 0.12));
}
</style>
